import { apiSlice } from "../../api-slice";
import { GetProposalParams } from "./proposal-api-request";
import { ConflictRequestData, ConflictRequestResponse, CreateProposalResponse, DefaultProposalResponse, ExistingProposalCompanyResponse, GetBookProposalResponse, GetProposalByIdResponse, GetProposalResponse, ProposalData, ProposalForRenew, ProposalForRenewResponse, ProposalListResponse } from "./proposal-api-state";

export interface GetProposalConflictParams {
    page?: number;
    per_page?: number;
    status?: string;
    email?: string;
    group_name?: string;
    product?: string;
    preparedBy?: string;
}
export type SearchFieldProposalConflict = "group_name" | "email" | "product" | "preparedBy" | "status";
export type ActionTypeConflictProposal = 'approve-conflict' | 'reject-conflict'

export const proposalAPI = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getBookedProposal: builder.query<ProposalListResponse, undefined | GetProposalParams>({
            query: (params) => ({
                url: "/api/ebam/booked-list",
                params: params ?? {},
            }),
            transformResponse: (response: GetBookProposalResponse) => response,
            transformErrorResponse: (response: any) => response.data,
            providesTags: [{ type: 'BookedProposal', id: 'LIST' }],
        }),
        getProposal: builder.query<ProposalListResponse, undefined | GetProposalParams>({
            query: (params) => ({
                url: "/api/financial-insurance/list",
                params: params ?? {},
            }),
            transformResponse: (response: GetProposalResponse) => response.data,
            transformErrorResponse: (response: any) => response.data,
            providesTags: [{ type: 'Proposal', id: 'LIST' }],
        }),
        getProposalByProposalId: builder.query<ProposalData, { proposalId: number }>({
            query: ({ proposalId }) => `/api/financial-insurance/${proposalId}`,
            transformResponse: (response: GetProposalByIdResponse) => response.data,
            transformErrorResponse: (response: any) => response.data,
            providesTags: (_result, _error, { proposalId }) => [
                { type: 'Proposal', id: proposalId },
                { type: 'Proposal', id: 'LIST' },
            ],
        }),
        createProposal: builder.mutation<CreateProposalResponse, any>({
            query: (newProposal) => ({
                url: `/api/financial-insurance/create`,
                method: 'POST',
                body: newProposal,
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: [
                { type: 'Proposal', id: 'LIST' },
                { type: 'RequestExistingGroup', id: 'LIST' },
            ],
        }),
        createProposalAsDraft: builder.mutation<CreateProposalResponse, any>({
            query: (newProposal) => ({
                url: `/api/financial-insurance/draft`,
                method: 'POST',
                body: newProposal,
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: [
                { type: 'Proposal', id: 'LIST' },
                // { type: 'RequestExistingGroup', id: 'LIST' },
            ],
        }),
        updateProposal: builder.mutation<CreateProposalResponse, { proposalId: number; updatedProposal: any }>({
            query: ({ proposalId, updatedProposal }) => ({
                url: `/api/financial-insurance/${proposalId}`,
                method: 'PUT',
                body: updatedProposal,
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: 'Proposal', id: proposalId },
                { type: 'Proposal', id: 'LIST' },
            ],
        }),
        getIsExistingCompanyProposal: builder.query<ExistingProposalCompanyResponse, { groupName: string }>({
            query: ({ groupName }) => `api/financial-insurance/check-group-name?group_name=${groupName}`,
            transformResponse: (response: ExistingProposalCompanyResponse) => response,
            transformErrorResponse: (response: any) => response.data,
            providesTags: [{ type: 'Proposal', id: 'LIST' }],
        }),
        updateStatusAsBooked: builder.mutation<any, { proposalId: number, effective_date: string }>({
            query: ({proposalId, effective_date}) => ({
                url: `/api/financial-insurance/${proposalId}/status/booked`,
                method: 'PUT',
                body: { effective_date },
            }),
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: 'Proposal', id: proposalId },
                { type: 'Proposal', id: 'LIST' },
            ],
        }),
        getProposalForRenew: builder.query<ProposalForRenew[], {secName: string}>({
            query: ({ secName }) => ({
                url: "/api/financial-insurance/renewal-lookup",
                params: { group_name: secName }
            }),
            transformResponse: (response: ProposalForRenewResponse) => response.data,
            transformErrorResponse: (response: any) => response.data,
        }),
        getProposalForRenewById: builder.query<ProposalData, { proposalID: number }>({
            query: ({ proposalID }) => ({
                url: "/api/financial-insurance/renewal-lookup",
                params: { id: proposalID  }
            }),
            transformResponse: (response: GetProposalByIdResponse) => response.data,
            transformErrorResponse: (response) => response.data,
        }),
        // CONFLICT PROPOSAL
        getProposalListConflict: builder.query<ConflictRequestData, undefined | GetProposalConflictParams>({
            query: (params) => ({
                url: "/api/financial-insurance/conflict-requests",
                params: params ?? {},
            }),
            transformResponse: (response: ConflictRequestResponse) => response.data,
            transformErrorResponse: (response) => response.data,
            providesTags: [{ type: 'RequestExistingGroup', id: 'LIST' }],
        }),
        approveRejectProposalConflict: builder.mutation<DefaultProposalResponse, { proposalId: number; actionType: ActionTypeConflictProposal}>({
            query: ({ proposalId, actionType }) => ({
                url: `/api/financial-insurance/${proposalId}/${actionType}`,
                method: 'PUT',
                body: {}
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: 'Proposal', id: proposalId },
                { type: 'Proposal', id: 'LIST' },
                { type: 'RequestExistingGroup', id: proposalId },
                { type: 'RequestExistingGroup', id: 'LIST' },
            ],
        }),
        updateSECorTINProposal: builder.mutation<DefaultProposalResponse, {proposalId: number, body: {company_tin: string; company_sec: string}}>({
            query: ({ proposalId, body }) => ({
                url: `/api/financial-insurance/${proposalId}/tin-sec`,
                method: 'PUT',
                body
            }),
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: 'Proposal', id: proposalId },
                { type: 'Proposal', id: 'LIST' },
            ],
        }),
    })
})

export const {
    useGetBookedProposalQuery,
    useCreateProposalMutation,
    useCreateProposalAsDraftMutation,
    useUpdateProposalMutation,
    useGetProposalQuery,
    // useGetAllProposalByPreparedIdQuery,
    useGetProposalByProposalIdQuery,
    useLazyGetProposalByProposalIdQuery,
    // useLazyViewProposalPDFQuery,
    // useDownloadProposalPDFMutation,
    // useUploadMasterCSVFileMutation,
    // useDownloadMasterFileCSVFileMutation,

    useGetIsExistingCompanyProposalQuery,
    useLazyGetIsExistingCompanyProposalQuery,

    useUpdateStatusAsBookedMutation,
    // RENEWAL
    useGetProposalForRenewQuery,
    useGetProposalForRenewByIdQuery,

    // CONFLICT PROPOSAL FOR APPROVAL
    useGetProposalListConflictQuery,
    // ACTION FOR CONFLICT PROPOSAL 
    useApproveRejectProposalConflictMutation,

    // UPDATE PROPOSAL EFFECTIVE DATE
    // useSetProposalEffectiveDateMutation,

    useUpdateSECorTINProposalMutation,
} = proposalAPI